import React from "react";
import { Sun, Eye, Shield, Flag, Flame, Smartphone, Video, CalendarClock } from "lucide-react";
import { ACADEMY_LOGO } from "../data/assets";
import { Page, DocHeader, Kicker, H1, SectionHead, Field, Check, PageStyle, GOLD, RED, GOLD_GRAD, RED_GRAD, PANEL } from "./print/academyDoc";

const WELCOME = "You haven't just signed up for soccer training; you have joined an elite technical academy. Our methodology combines high-repetition field instruction with digital video analysis to accelerate player development.";
const FIVE_OS = [
  [Sun, "Opportunity", "Every session, touch, and mistake is a chance to improve.", "Never waste a training session."],
  [Eye, "Observation", "See the game before it happens.", "Play with your eyes before your feet."],
  [Shield, "Ownership", "Own your development.", "Your progress is your responsibility."],
  [Flag, "Optimism", "Confidence is trained.", "Believe before you achieve."],
  [Flame, "Outwork", "Talent opens doors; work ethic keeps them open.", "Outwork yesterday."],
];
const STEPS = [
  [Smartphone, "Claim Player Account", "Visit hub.nextosoccer.com and log in with your primary email."],
  [Video, "Complete Initial Baseline Video", "Upload a 30-second clip performing continuous toe taps and foundations to establish your initial skill tier (Bronze, Silver, Gold, or Elite)."],
  [CalendarClock, "Weekly Video Homework Submission", "Homework is assigned every Sunday evening in the Hub. Mandatory video proof must be uploaded before your field session begins."],
];
const OUTCOMES = [
  ["Check in with purpose", "Your player starts knowing their single focus for the day — homework proof already on the Hub."],
  ["Move like an athlete", "Agility, balance and change of direction with eyes up."],
  ["Hundreds of quality touches", "The same movements with the ball — light, controlled, both feet."],
  ["Live feedback on homework", "Coach Barker watches assigned reps live and corrects mechanics on the spot."],
  ["Master the target move", "Slow reps, game speed, then against a defender — both feet."],
  ["Finish what they start", "The skill move connects straight into a strike on goal."],
  ["Leave with a win", "Every session ends with a 60-second challenge — score logged to the Hub."],
];
const PROMISE = ["I will train with purpose.", "I will develop both feet.", "I will embrace challenges.", "I will compete with integrity.", "I will respect the game.", "I will never stop improving."];
const FOOTER = "New Client Onboarding Guide";

const PageOne = () => (
  <Page n={1} footer={FOOTER} testid="onboarding-page-1">
    <DocHeader title="New Client Onboarding Guide" />
    <div className="mt-5 flex items-start justify-between gap-6">
      <div className="flex-1 min-w-0">
        <Kicker>Welcome to the Academy</Kicker>
        <H1 size={40}>You've joined an<br /><span style={{ color: GOLD }}>elite technical academy.</span></H1>
      </div>
      <img src={ACADEMY_LOGO} alt="" style={{ height: 84, filter: `drop-shadow(0 0 16px ${GOLD}55)` }} />
    </div>
    <div data-testid="onboarding-welcome" className="mt-4 rounded-lg border-l-[3px] px-5 py-4 text-[11.5px] leading-relaxed text-white/85" style={{ borderColor: GOLD, background: `linear-gradient(120deg, ${GOLD}14, ${PANEL})` }}>{WELCOME}</div>

    <SectionHead n={1} sub="Every Next O player is held to five core commitments on and off the pitch">The 5 O's Methodology · The Mindset</SectionHead>
    <div className="space-y-2">
      {FIVE_OS.map(([Icon, t, d, s], i) => (
        <div key={t} className="flex items-center gap-4 rounded-lg border px-3.5 py-2.5" style={{ borderColor: `${GOLD}44`, background: PANEL }}>
          <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 font-cover text-xl text-black" style={{ background: GOLD_GRAD }}>{i + 1}</div>
          <div className="w-[1.35in] shrink-0 flex items-center gap-2 font-headings text-[16px] uppercase tracking-wide leading-none"><Icon size={13} style={{ color: GOLD }} /> {t}</div>
          <div className="flex-1 text-[10px] text-white/70 leading-snug">{d}</div>
          <div className="w-[2.05in] shrink-0 rounded-md border px-3 py-1.5 font-headings text-[11px] uppercase tracking-wide leading-tight" style={{ borderColor: `${RED}66`, background: `${RED}14` }}>{s}</div>
        </div>
      ))}
    </div>

    <SectionHead n={2} sub="hub.nextosoccer.com · complete these 3 steps immediately">Platform Setup and Digital Hub</SectionHead>
    <div className="grid grid-cols-3 gap-2.5">
      {STEPS.map(([Icon, t, d], i) => (
        <div key={t} className="relative rounded-lg border p-3.5 overflow-hidden" style={{ borderColor: `${GOLD}44`, background: PANEL }}>
          <div className="absolute right-3 top-1 font-cover text-5xl" style={{ color: `${GOLD}1f` }}>{i + 1}</div>
          <div className="font-mono text-[7.5px] uppercase tracking-[0.35em]" style={{ color: GOLD }}>Step {i + 1}</div>
          <div className="flex items-center gap-2 font-headings text-[14px] uppercase tracking-wide leading-tight mt-1.5"><Icon size={14} style={{ color: RED }} className="shrink-0" /> {t}</div>
          <p className="text-[9.5px] text-white/70 mt-2 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
    <div data-testid="onboarding-standard" className="mt-3 flex items-center gap-3 rounded-md border px-4 py-2.5" style={{ borderColor: `${RED}88`, background: `${RED}14` }}>
      <span className="font-mono text-[7.5px] font-bold uppercase tracking-[0.35em] shrink-0" style={{ color: "#ff8a8e" }}>Standard</span>
      <span className="font-headings text-[13px] uppercase tracking-wide leading-tight">No video proof = player starts session with a progression level handicap.</span>
    </div>
  </Page>
);

const PageTwo = () => (
  <Page n={2} footer={FOOTER} testid="onboarding-page-2">
    <DocHeader title="New Client Onboarding Guide" />
    <SectionHead n={3} sub="Sixty minutes, zero wasted touches — what your player gains every field session">What Every Session Delivers</SectionHead>
    <div className="space-y-2">
      {OUTCOMES.map(([t, d], i) => (
        <div key={t} data-testid={`onboarding-outcome-${i + 1}`} className="flex items-stretch rounded-lg border overflow-hidden" style={{ borderColor: i === 6 ? `${GOLD}88` : "rgba(255,255,255,0.12)", background: PANEL }}>
          <div className="w-14 flex items-center justify-center font-cover text-3xl shrink-0" style={{ background: i === 6 ? GOLD_GRAD : RED_GRAD, color: i === 6 ? "#000" : "#fff" }}>{i + 1}</div>
          <div className="w-[2.6in] shrink-0 flex items-center px-4 font-headings text-[13px] uppercase tracking-wide leading-tight border-r" style={{ borderColor: `${GOLD}2a` }}>{t}</div>
          <div className="flex-1 flex items-center px-4 py-3 text-[10px] text-white/70 leading-snug">{d}</div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-between font-mono text-[7.5px] uppercase tracking-[0.3em] text-white/45"><span>Same structure every week · scaled to your player's tier</span><span style={{ color: GOLD }}>Total session · 60 min</span></div>

    <SectionHead n={4} sub="Read, check and sign — the standard is shared by player, parent and coach">Parent and Player Commitment Agreement</SectionHead>
    <div className="rounded-lg border p-5" style={{ borderColor: `${GOLD}66`, background: `${GOLD}0d` }}>
      <div className="font-mono text-[8px] font-bold uppercase tracking-[0.4em]" style={{ color: GOLD }}>The Next O Promise</div>
      <div className="text-[9.5px] text-white/55 mt-1">This is the standard you hold yourself to — on and off the field.</div>
      <div data-testid="onboarding-promise" className="grid grid-cols-2 gap-x-8 gap-y-5 mt-5">{PROMISE.map((p) => <Check key={p} size={13}><span className="text-[11px]">{p}</span></Check>)}</div>
    </div>
    <div data-testid="onboarding-signatures" className="mt-auto rounded-lg border p-5" style={{ borderColor: "rgba(255,255,255,0.14)", background: PANEL }}>
      <div className="grid grid-cols-[3fr_2fr] gap-x-10 gap-y-6">
        <Field label="Player Signature" h={30} />
        <Field label="Date" h={30} />
        <Field label="Parent Signature" h={30} />
        <div>
          <div className="font-mono text-[7px] font-bold uppercase tracking-[0.3em]" style={{ color: GOLD }}>Coach</div>
          <div className="font-headings text-[15px] uppercase tracking-wide leading-none mt-2.5 pb-1.5 border-b" style={{ borderColor: "rgba(255,255,255,0.35)" }}>Coach Barker <span className="text-white/35 mx-1">/</span> Next O</div>
        </div>
      </div>
    </div>
    <div className="mt-4 flex items-center justify-between">
      <div className="font-mono text-[8px] uppercase tracking-[0.35em] text-white/45">Train · Develop · Compete</div>
      <div className="font-headings text-[13px] uppercase tracking-wide" style={{ color: GOLD }}>hub.nextosoccer.com</div>
    </div>
  </Page>
);

// /print/onboarding-guide → New Client Onboarding Guide (2 × 8.5×11, Academy palette), rendered by scripts/render_doc.py
export default function PrintOnboardingGuide() {
  return (
    <div data-testid="onboarding-doc" className="bg-black" style={{ width: "8.5in" }}>
      <PageStyle />
      <PageOne /><PageTwo />
    </div>
  );
}
